import { useState } from 'react';
import type { ReactNode } from 'react';
import type { PanelType, ResolutionTierKey } from '../../../types/Monitor';
import { PANEL_TYPE_LABELS } from '../../../constants';
import { RangeSlider } from '../../ui/RangeSlider';
import styles from './MonitorFilters.module.css';

// ── Constants ──────────────────────────────────────────────────────────────

const PANEL_TYPES: PanelType[] = ['IPS', 'VA', 'OLED', 'Mini-LED', 'TN'];

const KNOWN_BRANDS = ['Apple', 'Asus', 'BenQ', 'Dell', 'LG', 'Samsung'];

const PORT_CATEGORIES = ['Thunderbolt', 'HDMI', 'DisplayPort', 'USB-C'] as const;

const RESOLUTION_TIERS: { key: ResolutionTierKey; label: string }[] = [
  { key: 'FHD', label: 'FHD (1080p)' },
  { key: 'QHD', label: 'QHD (1440p)' },
  { key: '4K',  label: '4K (2160p)' },
  { key: '5K',  label: '5K (2880p+)' },
];

export const DIAGONAL_MIN  = 21;
export const DIAGONAL_MAX  = 49;
export const REFRESH_MIN   = 60;
export const REFRESH_MAX   = 240;
export const BRIGHTNESS_MAX = 1200;

// ── Types ──────────────────────────────────────────────────────────────────

export interface SidebarFilters {
  brands: string[];
  panelTypes: PanelType[];
  diagonalRange: [number, number];
  resolutionTiers: ResolutionTierKey[];
  /** 60 = no filter (every monitor qualifies) */
  minRefreshRate: number;
  /** 0 = no filter */
  minBrightnessNits: number;
  requiredPorts: string[];
  maxPriceUSD: number | undefined;
  hdrOnly: boolean;
}

interface MonitorFiltersProps {
  filters: SidebarFilters;
  onChange: (filters: SidebarFilters) => void;
}

// ── Helpers ────────────────────────────────────────────────────────────────

function hasActiveFilters(f: SidebarFilters): boolean {
  return (
    f.brands.length > 0 ||
    f.panelTypes.length > 0 ||
    f.diagonalRange[0] > DIAGONAL_MIN ||
    f.diagonalRange[1] < DIAGONAL_MAX ||
    f.resolutionTiers.length > 0 ||
    f.minRefreshRate > REFRESH_MIN ||
    f.minBrightnessNits > 0 ||
    f.requiredPorts.length > 0 ||
    f.maxPriceUSD !== undefined ||
    f.hdrOnly
  );
}

function toggle<T>(arr: T[], item: T): T[] {
  return arr.includes(item) ? arr.filter((x) => x !== item) : [...arr, item];
}

// ── Sub-components ─────────────────────────────────────────────────────────

interface SectionProps {
  id: string;
  title: string;
  collapsed: Set<string>;
  onToggle: (id: string) => void;
  children: ReactNode;
}

function CollapsibleSection({ id, title, collapsed, onToggle, children }: SectionProps) {
  const open = !collapsed.has(id);
  return (
    <section className={styles.section}>
      <button
        type="button"
        className={styles.sectionHeader}
        onClick={() => onToggle(id)}
        aria-expanded={open}
      >
        <span className={styles.sectionTitle}>{title}</span>
        <svg
          className={`${styles.chevron} ${open ? '' : styles.chevronClosed}`}
          width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"
        >
          <path d="M2 8L6 4L10 8" stroke="currentColor" strokeWidth="1.5"
            strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && <div className={styles.sectionBody}>{children}</div>}
    </section>
  );
}

interface FilterSliderProps {
  min: number;
  max: number;
  step?: number;
  value: number;
  formatLabel: (v: number) => string;
  onChange: (v: number) => void;
}

function FilterSlider({ min, max, step = 1, value, formatLabel, onChange }: FilterSliderProps) {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div className={styles.sliderRoot}>
      <div className={styles.sliderWrapper}>
        <div className={styles.sliderTrack}>
          <div className={styles.sliderFill} style={{ width: `${pct}%` }} />
        </div>
        <input
          type="range"
          className={styles.sliderInput}
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          aria-valuetext={formatLabel(value)}
        />
      </div>
      <div className={styles.sliderLabel}>
        <span>{formatLabel(value)}</span>
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────

export function MonitorFilters({ filters, onChange }: MonitorFiltersProps) {
  // Collapse brand, panel type, and inputs by default
  const [collapsed, setCollapsed] = useState<Set<string>>(
    () => new Set(['brand', 'panel', 'ports'])
  );

  function toggleSection(id: string) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function clearAll() {
    onChange({
      brands: [],
      panelTypes: [],
      diagonalRange: [DIAGONAL_MIN, DIAGONAL_MAX],
      resolutionTiers: [],
      minRefreshRate: REFRESH_MIN,
      minBrightnessNits: 0,
      requiredPorts: [],
      maxPriceUSD: undefined,
      hdrOnly: false,
    });
  }

  const section = (id: string, title: string, children: ReactNode) => (
    <CollapsibleSection id={id} title={title} collapsed={collapsed} onToggle={toggleSection}>
      {children}
    </CollapsibleSection>
  );

  return (
    <div className={styles.panel}>
      <div className={styles.panelHeader}>
        <span className={styles.panelTitle}>Filters</span>
        {hasActiveFilters(filters) && (
          <button className={styles.clearBtn} type="button" onClick={clearAll}>
            Clear all
          </button>
        )}
      </div>

      {section('size', 'Screen Size',
        <RangeSlider
          min={DIAGONAL_MIN} max={DIAGONAL_MAX} step={1}
          value={filters.diagonalRange}
          formatLabel={(v) => `${v}"`}
          onChange={(range) => onChange({ ...filters, diagonalRange: range })}
        />
      )}

      {section('brand', 'Brand',
        <div className={styles.checkGroup}>
          {KNOWN_BRANDS.map((brand) => (
            <label key={brand} className={styles.checkLabel}>
              <input type="checkbox" className={styles.checkbox}
                checked={filters.brands.includes(brand)}
                onChange={() => onChange({ ...filters, brands: toggle(filters.brands, brand) })}
              />
              <span>{brand}</span>
            </label>
          ))}
        </div>
      )}

      {section('panel', 'Panel Type',
        <div className={styles.checkGroup}>
          {PANEL_TYPES.map((type) => (
            <label key={type} className={styles.checkLabel}>
              <input type="checkbox" className={styles.checkbox}
                checked={filters.panelTypes.includes(type)}
                onChange={() => onChange({ ...filters, panelTypes: toggle(filters.panelTypes, type) })}
              />
              <span>{PANEL_TYPE_LABELS[type]}</span>
            </label>
          ))}
        </div>
      )}

      {section('resolution', 'Resolution',
        <div className={styles.checkGroup}>
          {RESOLUTION_TIERS.map(({ key, label }) => (
            <label key={key} className={styles.checkLabel}>
              <input type="checkbox" className={styles.checkbox}
                checked={filters.resolutionTiers.includes(key)}
                onChange={() => onChange({ ...filters, resolutionTiers: toggle(filters.resolutionTiers, key) })}
              />
              <span>{label}</span>
            </label>
          ))}
        </div>
      )}

      {section('refresh', 'Refresh Rate',
        <FilterSlider
          min={REFRESH_MIN} max={REFRESH_MAX} step={1}
          value={filters.minRefreshRate}
          formatLabel={(v) => v === REFRESH_MIN ? 'Any' : `${v}+ Hz`}
          onChange={(v) => onChange({ ...filters, minRefreshRate: v })}
        />
      )}

      {section('brightness', 'Brightness',
        <FilterSlider
          min={0} max={BRIGHTNESS_MAX} step={50}
          value={filters.minBrightnessNits}
          formatLabel={(v) => v === 0 ? 'Any' : `${v}+ nits`}
          onChange={(v) => onChange({ ...filters, minBrightnessNits: v })}
        />
      )}

      {section('ports', 'Inputs',
        <div className={styles.checkGroup}>
          {PORT_CATEGORIES.map((cat) => (
            <label key={cat} className={styles.checkLabel}>
              <input type="checkbox" className={styles.checkbox}
                checked={filters.requiredPorts.includes(cat)}
                onChange={() => onChange({ ...filters, requiredPorts: toggle(filters.requiredPorts, cat) })}
              />
              <span>{cat}</span>
            </label>
          ))}
        </div>
      )}

      {section('price', 'Max Price',
        <div className={styles.priceRow}>
          <span className={styles.currencySign}>$</span>
          <input
            type="number"
            className={styles.priceInput}
            placeholder="Any"
            min={0}
            value={filters.maxPriceUSD ?? ''}
            onChange={(e) => {
              const val = e.target.value;
              onChange({ ...filters, maxPriceUSD: val === '' ? undefined : Number(val) });
            }}
          />
        </div>
      )}

      {section('hdr', 'Other',
        <label className={styles.checkLabel}>
          <input type="checkbox" className={styles.checkbox}
            checked={filters.hdrOnly}
            onChange={(e) => onChange({ ...filters, hdrOnly: e.target.checked })}
          />
          <span>HDR only</span>
        </label>
      )}
    </div>
  );
}
